import { useDialogContext, useStoreState } from "@ariakit/react";
import { LetterheadSubmitError, useForm } from "@indietabletop/appkit";
import type { SpaceGits } from "@indietabletop/types";
import { toast } from "react-hot-toast";
import { database } from "../../database.ts";
import { EditMode } from "../../engine/EditMode.ts";
import { caps } from "../fmt.ts";
import { FormDialog, FormDialogActions } from "../FormDialog/FormDialog.tsx";
import { FormDialogSubmitButton } from "../forms/DialogSubmitButton.tsx";
import {
  FormCheckboxOption,
  FormOption,
  FormOptions,
} from "../forms/FormTextField.tsx";
import { PremiumContentNote } from "../PremiumContentNote/PremiumContentNote.tsx";
import { CannotRemoveNote } from "./FormNote.tsx";
import { useVehicleContext } from "./ModelContext.tsx";
import { RichContent } from "./PremiumContent.tsx";
import { PreviewBanner } from "./StashPreviewBanner.tsx";
import { formOption } from "./style.css.ts";

const DELIMITER = ".";

function toCompositeId(ref: SpaceGits.RepairRef) {
  return `${ref.locationId}${DELIMITER}${ref.id}`;
}

function fromCompositeId(compositeId: string) {
  const [locationId, id] = compositeId.split(DELIMITER) as [string, string];
  return { locationId, id };
}

function toRepairRefs(repairCompositeIds: string[]) {
  return repairCompositeIds.map(fromCompositeId);
}

export function ChooseRepairsDialog() {
  const vehicle = useVehicleContext();
  const dialog = useDialogContext();
  const repairLocations = vehicle.ruleset.repairLocations.toArray();
  const initialCompositeIds = vehicle.repairRefs.map(toCompositeId);
  const canRemoveExisting = vehicle.can(EditMode.CHANGE, "repairs");
  const { mode } = vehicle.gang;

  const { form, submitName } = useForm({
    defaultValues: {
      isPaid: false,
      repairsCompositeIds: initialCompositeIds,
    },

    async onSubmit({ values }) {
      const repairRefs = toRepairRefs(values.repairsCompositeIds);
      const next = vehicle.addRepairs(repairRefs, values.isPaid);
      return await database.gangs.updateOne(next.gang.data);
    },

    onSuccess() {
      dialog?.hide();
      toast("Saved");
    },
  });

  const values = useStoreState(form, (store) => store.values);
  const repairRefs = toRepairRefs(values.repairsCompositeIds);
  const draftVehicle = vehicle.addRepairs(repairRefs, values.isPaid);

  return (
    <FormDialog
      overline="Krankshaft's"
      heading="Vehicle Damage Report"
      form={form}
    >
      <PreviewBanner draftGang={draftVehicle.gang} where="at the shop" />

      <PremiumContentNote />

      {vehicle.can(EditMode.ADD, "repairs") && (
        <FormOptions label="Repair Fees">
          <FormCheckboxOption
            label="Got proper job done?"
            description="Select if you rolled for repairs twice and picked your preferred option."
            name={form.names.isPaid}
            type="checkbox"
          />
        </FormOptions>
      )}

      {repairLocations.map((location) => {
        return (
          <FormOptions key={location.id} label={location.name}>
            {location.repairs
              .toArray()
              .filter((repair) => {
                return (
                  mode.isCampaignMode ||
                  !repair.effects.some((e) => e.type === "REMOVE_MODEL")
                );
              })
              .map((repair) => {
                const { id, name, description, location } = repair;

                const compositeId = toCompositeId({
                  id,
                  locationId: location.id,
                });

                const cannotRemove =
                  !canRemoveExisting &&
                  initialCompositeIds.includes(compositeId);

                return (
                  <FormOption
                    type="checkbox"
                    key={compositeId}
                    name={form.names.repairsCompositeIds}
                    value={compositeId}
                    label={name}
                    info={caps(values.isPaid ? location.cost : 0)}
                    disabled={cannotRemove}
                    description={
                      <>
                        <RichContent>{description}</RichContent>

                        {cannotRemove && (
                          <div className={formOption.extras}>
                            <CannotRemoveNote what="REPAIR" />
                          </div>
                        )}
                      </>
                    }
                  />
                );
              })}
          </FormOptions>
        );
      })}

      <FormDialogActions>
        <LetterheadSubmitError name={submitName} />

        <FormDialogSubmitButton>Save</FormDialogSubmitButton>
      </FormDialogActions>
    </FormDialog>
  );
}
